import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Markdown from 'markdown-to-jsx';
import { format, parseISO, subDays } from 'date-fns';
import IncidentTimeline from './IncidentTimeline';
import '../../../styles/features/incidents/IncidentHistory.css';
import { getPublicIncidentById } from '../../../shared/api/publicApi';

const STATUS_LABELS = {
  investigating: 'Investigating',
  identified: 'Identified',
  monitoring: 'Monitoring',
  resolved: 'Resolved',
};

const HISTORY_DAYS = 90;
const INITIAL_RESOLVED_COUNT = 5;

function IncidentHistory({ incidents = [] }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(new Set());
  const [fullUpdates, setFullUpdates] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [showAllResolved, setShowAllResolved] = useState(false);

  const cutoff = subDays(new Date(), HISTORY_DAYS);

  const activeIncidents = incidents.filter(i => !i.resolved_at);
  const resolvedIncidents = incidents
    .filter(i => i.resolved_at && new Date(i.start_time) > cutoff)
    .sort((a, b) => new Date(b.start_time) - new Date(a.start_time));

  const visibleResolved = showAllResolved
    ? resolvedIncidents
    : resolvedIncidents.slice(0, INITIAL_RESOLVED_COUNT);

  const toggleExpand = useCallback(
    async (incident) => {
      const id = incident.id;

      setExpanded(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });

      if (!fullUpdates[id]) {
        try {
          setLoadingId(id);
          const res = await getPublicIncidentById(id);
          setFullUpdates(prev => ({
            ...prev,
            [id]: {
              updates: res.data.updates || [],
              post_mortem: res.data.post_mortem || null,
            },
          }));
        } catch (err) {
          console.error('Failed to load incident updates:', err);
        } finally {
          setLoadingId(null);
        }
      }
    },
    [fullUpdates]
  );

  const calculateDuration = (incident) => {
    if (!incident.resolved_at) return 'Ongoing';

    const minutes = Math.round(
      (new Date(incident.resolved_at) - new Date(incident.start_time)) / 60000
    );

    if (minutes < 60) return `${minutes}m`;
    if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
    return `${Math.round(minutes / 1440)}d`;
  };

  const formatTimeRange = (incident, isResolved) => {
    if (isResolved) {
      return `${format(parseISO(incident.start_time), 'MMMM d, yyyy')} at ${format(
        parseISO(incident.start_time),
        'h:mm a'
      )} – ${format(parseISO(incident.resolved_at), 'h:mm a')}`;
    }

    return `${format(parseISO(incident.start_time), 'MMMM d, yyyy')} at ${format(
      parseISO(incident.start_time),
      'h:mm a'
    )}`;
  };

  const latestPreview =
    (incident) => incident?.latest_update?.message?.split('\n')[0] || incident?.description || '';

  const renderAffectedServices = (incident) => {
    if (!incident.endpoints?.length) return null;

    return (
      <div className="affected-services">
        <span className="affected-label">Affected services</span>
        <div className="service-tags">
          {incident.endpoints.map((ep) => (
            <span key={ep.id} className="service-tag">
              {ep.name}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderIncidentCard = (incident, isResolved = false) => {
    const isExpanded = expanded.has(incident.id);
    const isLoading = loadingId === incident.id;
    const hasUpdates = incident.latest_update != null;
    const detail = fullUpdates[incident.id];
    const updates =
      detail?.updates ?? (incident.latest_update ? [incident.latest_update] : []);
    const postMortem = detail?.post_mortem ?? incident.post_mortem ?? null;
    const duration = calculateDuration(incident);
    const preview = latestPreview(incident);

    const clickableProps = hasUpdates
      ? {
          onClick: () => toggleExpand(incident),
          role: 'button',
          'aria-expanded': isExpanded,
          style: { cursor: 'pointer' },
        }
      : {
          style: { cursor: 'default' },
        };

    return (
      <article
        key={incident.id}
        className={`incident-item ${
          isResolved ? 'incident-item--resolved' : 'incident-item--active'
        } ${isExpanded ? 'incident-item--expanded' : ''}`}
      >
        <div className="incident-header" {...clickableProps}>
          <div className="incident-main-content">
            {incident.status && (
              <div className="incident-status-badge-wrapper">
                <span className={`incident-status-badge status-badge-${incident.status}`}>
                  {STATUS_LABELS[incident.status] || incident.status}
                </span>
              </div>
            )}

            <div className="incident-title-row">
              <a
                href={`/incident/${incident.id}`}
                className="incident-title-link"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/incident/${incident.id}`);
                }}
                title="Permalink to this incident"
              >
                {incident.title}
              </a>

              {incident.source === 'auto' && (
                <span className="incident-pill pill-auto">Auto</span>
              )}
            </div>

            <p className="incident-time">{formatTimeRange(incident, isResolved)}</p>
          </div>

          <div className="incident-right-section">
            <span
              className={`duration-badge ${
                isResolved ? 'duration-badge--resolved' : 'duration-badge--active'
              }`}
            >
              {duration}
            </span>

            {hasUpdates && (
              <span className="expand-toggle" aria-hidden="true">
                {isExpanded ? '▲' : '▼'}
              </span>
            )}
          </div>
        </div>

        {!isExpanded && (
          <div className="incident-body">
            {preview && (
              <div className="incident-latest-update">
                <Markdown options={{ disableParsingRawHTML: true, forceInline: true }}>
                  {preview}
                </Markdown>
              </div>
            )}

            {renderAffectedServices(incident)}
          </div>
        )}

        {isExpanded && (
          <div className="incident-expanded">
            {preview && (
              <div className="incident-latest-update">
                <Markdown options={{ disableParsingRawHTML: true }}>
                  {preview}
                </Markdown>
              </div>
            )}

            {renderAffectedServices(incident)}

            <div className="incident-timeline-section">
              {isLoading ? (
                <p className="timeline-loading">Loading history…</p>
              ) : (
                <IncidentTimeline updates={updates} postMortem={postMortem} />
              )}
            </div>
          </div>
        )}
      </article>
    );
  };

  if (activeIncidents.length === 0 && resolvedIncidents.length === 0) {
    return (
      <div className="incident-history">
        <h3 className="incident-history__title">Incident History</h3>
        <div className="no-incidents">
          <p>No incidents in the past {HISTORY_DAYS} days. ✓</p>
        </div>
      </div>
    );
  }

  return (
    <div className="incident-history">
      <h3 className="incident-history__title">Incident History</h3>

      {activeIncidents.length > 0 && (
        <div className="incidents-section incidents-section--active">
          <div className="section-header section-header--active">
            <span>Active Incidents</span>
            <span className="section-header__count">{activeIncidents.length}</span>
          </div>

          <div className="incidents-list">
            {activeIncidents.map((incident) => renderIncidentCard(incident, false))}
          </div>
        </div>
      )}

      {resolvedIncidents.length > 0 && (
        <div className="incidents-section incidents-section--resolved">
          <div className="section-header section-header--resolved">
            <span>Resolved - Past {HISTORY_DAYS} Days</span>
            <span className="section-header__count">{resolvedIncidents.length}</span>
          </div>

          <div className="incidents-list">
            {visibleResolved.map((incident) => renderIncidentCard(incident, true))}
          </div>

          {resolvedIncidents.length > INITIAL_RESOLVED_COUNT && (
            <button
              className="show-more-btn"
              onClick={() => setShowAllResolved((v) => !v)}
            >
              {showAllResolved
                ? 'Show less'
                : `Show ${resolvedIncidents.length - INITIAL_RESOLVED_COUNT} more resolved`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default IncidentHistory;
